import Dexie, { type Table } from 'dexie';
import { ALL_COLLECTIONS, type AppNotification, type FieldConflict, type WireRecord } from '@app/shared';

/**
 * The on-device database (IndexedDB via Dexie). It holds a replica of every
 * record the server has authorized this user to see, the outbox of local
 * changes not yet synced, files waiting to upload, and cached session data so
 * the app opens offline. It is wiped on sign-out.
 */

export type LocalRecord = WireRecord;

export interface OutboxEntry {
  seq?: number;
  mid: string;
  familyId: string;
  collection: string;
  recordId: string;
  op: 'upsert' | 'delete' | 'restore';
  patch?: Record<string, unknown>;
  baseRev: number;
  clientTime: number;
  state: 'queued' | 'sending';
  attempts: number;
}

export interface LocalConflict {
  /** `${collection}:${recordId}:${field}` or `${collection}:${recordId}:deleted` */
  id: string;
  familyId: string;
  collection: string;
  recordId: string;
  kind: 'field' | 'deleted';
  field?: string;
  theirs?: unknown;
  yours?: unknown;
  /** Snapshot of the local record when it was deleted elsewhere (to offer restore). */
  snapshot?: LocalRecord;
  createdAt: string;
}

export interface PendingUpload {
  fileId: string;
  familyId: string;
  collection: string;
  recordId: string;
  name: string;
  type: string;
  blob: Blob;
  thumb: Blob | null;
  createdAt: string;
  state: 'queued' | 'failed';
  error?: string;
}

export interface MetaEntry {
  key: string;
  value: unknown;
}

/** Extra indexes per collection for common lookups. */
const EXTRA_INDEXES: Record<string, string> = {
  list_items: 'listId',
  task_completions: 'taskId',
  photos: 'albumId',
  documents: 'folderId',
  pet_records: 'petId',
  maintenance_items: 'assetId',
  goal_entries: 'goalId',
  comments: '[targetCollection+targetId]',
  meal_entries: 'date',
};

export class LocalDb extends Dexie {
  outbox!: Table<OutboxEntry, number>;
  meta!: Table<MetaEntry, string>;
  conflicts!: Table<LocalConflict, string>;
  uploads!: Table<PendingUpload, string>;
  notifications!: Table<AppNotification, string>;

  constructor(name = 'app-local') {
    super(name);
    const stores: Record<string, string> = {
      outbox: '++seq, mid, familyId, [collection+recordId]',
      meta: 'key',
      conflicts: 'id, familyId',
      uploads: 'fileId, familyId, recordId',
      notifications: 'id, createdAt',
    };
    for (const c of ALL_COLLECTIONS) {
      const extra = EXTRA_INDEXES[c.name];
      stores[c.name] = `id, familyId${extra ? `, ${extra}` : ''}`;
    }
    this.version(1).stores(stores);
  }

  records(collection: string): Table<LocalRecord, string> {
    return this.table(collection);
  }
}

export let db = new LocalDb();

export async function getMeta<T>(key: string): Promise<T | undefined> {
  return (await db.meta.get(key))?.value as T | undefined;
}

export async function setMeta(key: string, value: unknown): Promise<void> {
  await db.meta.put({ key, value });
}

export const META = {
  me: 'session:me',
  family: (id: string) => `family:${id}`,
  cursor: (id: string) => `sync:cursor:${id}`,
  epoch: (id: string) => `sync:epoch:${id}`,
  serverOffset: 'sync:serverOffset',
  lastSync: (id: string) => `sync:last:${id}`,
} as const;

/** Remove everything for one family (membership ended or family deleted). */
export async function wipeFamily(familyId: string): Promise<void> {
  await db.transaction('rw', db.tables, async () => {
    for (const c of ALL_COLLECTIONS) await db.records(c.name).where('familyId').equals(familyId).delete();
    await db.outbox.where('familyId').equals(familyId).delete();
    await db.conflicts.where('familyId').equals(familyId).delete();
    await db.uploads.where('familyId').equals(familyId).delete();
    await db.meta.bulkDelete([META.family(familyId), META.cursor(familyId), META.epoch(familyId), META.lastSync(familyId)]);
  });
}

/** Sign-out / account switch: delete the whole local database and cached files. */
export async function wipeAll(): Promise<void> {
  db.close();
  await Dexie.delete(db.name);
  db = new LocalDb(db.name);
  await db.open();
  if (typeof caches !== 'undefined') {
    for (const name of await caches.keys()) if (name.startsWith('file-')) await caches.delete(name);
  }
}

export type { FieldConflict };
